import { Box, useTheme } from "@mui/material";
import { noop } from "lodash-es";
import { CSSProperties, useMemo } from "react";
import { DateLocalizer, EventPropGetter } from "react-big-calendar";

import { clsxc } from "@/common/clsxc";

import { GenericCalendarEntry, rbcEventPropGetterFactory } from "../Calendar";
import { rbcEventContentStyles, rbcEventStyles } from "../Calendar.styles";
import { RBCEventCustom } from "./RBCEventCustom";
import { RBCEventWrapperCustom } from "./RBCEventWrapperCustom";

interface Props<TEntry extends GenericCalendarEntry> {
  event: TEntry;
  localizer: DateLocalizer;
  className?: string;
  style?: CSSProperties;
  contentProps?: { style?: CSSProperties };
  onSelect?: ((event: TEntry, e: React.SyntheticEvent<HTMLElement>) => void) | undefined;
}

/** Simulated replacement of the RBC event component (.rbc-event).
 * It's simulated because RBC constructs event component internally
 * and doesn't allow to pass custom implementation, but only tweak it via CSS styles or event 'style' prop.
 */
export function RBCEventSimulatedCustom<TEntry extends GenericCalendarEntry>(props: Props<TEntry>) {
  // console.log("RBCEventSimulatedCustom", props);

  const theme = useTheme();

  const eventPropGetter = useMemo<EventPropGetter<TEntry>>(
    () => rbcEventPropGetterFactory({ theme }),
    [theme],
  );
  const eventProps = useMemo<ReturnType<EventPropGetter<TEntry>>>(
    () => eventPropGetter(props.event, props.event.start, props.event.end, false),
    [eventPropGetter],
  );

  return (
    <Box
      component='div'
      className={clsxc(
        props.className,
        eventProps.className,
        "rbc-event",
        "rbc-event-simulated-custom",
      )}
      style={{
        ...rbcEventStyles,
        ...eventProps.style,
        ...props.style,
      }}
      onClick={(e) => props.onSelect?.(props.event, e)}
    >
      <Box
        component='div'
        className={clsxc(
          props.className,
          "rbc-event-content",
          "rbc-event-content-simulated-custom",
        )}
        style={{
          ...rbcEventContentStyles,
          ...props.contentProps?.style,
        }}
      >
        {/* Mock most of the props */}
        <RBCEventWrapperCustom
          style={undefined}
          className={undefined}
          event={props.event}
          isRtl={false}
          getters={{
            eventProp: undefined,
            slotProp: undefined,
            dayProp: undefined,
          }}
          accessors={{
            title: undefined,
            tooltip: undefined,
            end: undefined,
            start: undefined,
          }}
          onClick={noop}
          onDoubleClick={noop}
          selected={false}
          label={undefined}
          continuesEarlier={false}
          continuesLater={false}
        >
          <RBCEventCustom
            event={props.event}
            title={props.event.title as string}
            continuesPrior={false}
            continuesAfter={false}
            isAllDay={undefined}
            localizer={props.localizer}
            slotStart={props.event.start}
            slotEnd={props.event.end}
          />
        </RBCEventWrapperCustom>
      </Box>
    </Box>
  );
}
